import { AUTO_DELIVERY_HOLD_HOURS, BUILDING_DEFINITIONS, BUILDING_NAMES, RESOURCE_NAMES, SURFACE_PLOT_IDS, SCRAP_RECYCLE_DAILY_LIMIT, SCRAP_RECYCLE_REFUND_MAX, SCRAP_RECYCLE_REFUND_MIN, SHIP_BUILD_REFUND_MAX, SHIP_BUILD_REFUND_MIN, SHIP_GROUP_MAX_ITEMS, SHIP_GROUP_MIN_ITEMS, SHIP_UPGRADE_MAX_LEVEL, SCIENCE_DEFINITIONS, getShipDefinition, isTeamPlanet, } from "@kaeliux/shared";
import { applyResourceCost, applyResourceRefund, applyResourceRefundAtRates, calculateNominalRefund, calculateNominalRefundAtRates, calculatePlanetTick, REFUND_RATES, } from "./resourceEngine.js";
import { calculateDockedPopulation, calculateFreePopulation, calculateShipBuildCost, calculateShipBuildTime, calculateShipUpgradeCost, calculateShipUpgradeRefundRate, calculateShipUpgradeTime, checkShipRequirements, computeShipBuildLane, isShipUpgradable, recycleScrapRequired, } from "./ships.js";
import { calculateBuildingUpgradeCost, canAffordCost, checkPrerequisites, computeBuildDurationSeconds, extractBuildingLevels, } from "./buildings.js";
import { calculateScienceCost, calculateScienceTime, checkScienceRequirements, canResearchAdditionalScience, getMaxScienceLevel, getScienceRequiredFreeEnergy, hasRequiredFreeEnergy, isScienceRushable, } from "./sciences.js";
import { calculateRushCost } from "./queueRush.js";
import { applyDerivedStats, computeProjectedBuildingLevels, withProjectedNextCosts, } from "./derivedStats.js";
import { calculateTeamPlanetFreePopulation, teamPlanetDockShips, teamPlanetUndockShips, } from "./teamPlanetEngine.js";
const RACES = ["humans", "robots", "axons"];
const ZONES = ["resource", "industry", "military"];
function teamShipBuildRace(event) {
    const lane = event.eventDetails.queueLane ?? "default";
    return RACES.includes(lane) ? lane : "humans";
}
/** Validation failure in the queue/cancel path — callers map `code` to transport errors. */
export class QueueError extends Error {
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "QueueError";
    }
}
function byEndDateThenId(a, b) {
    return a.endDate.getTime() - b.endDate.getTime() || a.eventId - b.eventId;
}
/**
 * When an event's effect actually resolves during replay — `endDate` for
 * everything except a Robots grouped shipBuild batch or a scrapRecycle batch
 * (issues #91 / #21), both of which resolve at their held-back `deliverAt`
 * instead. Used ONLY by `replayEventsToNow`'s dispatch ordering/cursor:
 * every other caller (lane tail-chaining, reflow, cancellation
 * elapsed-time math) must keep keying off `endDate` — construction/
 * processing-finish, not delivery — since the delivery hold must never
 * affect shipyard-slot or recycling-slot occupancy.
 */
function checkpointTime(e) {
    if (e.eventType === "shipBuild" && e.eventDetails.deliverAt)
        return e.eventDetails.deliverAt;
    if (e.eventType === "scrapRecycle" && e.eventDetails.deliverAt)
        return e.eventDetails.deliverAt;
    return e.endDate;
}
function isPendingBuild(e) {
    return e.eventType === "buildingBuild" && !e.cancelled;
}
function isPendingResearch(e) {
    return e.eventType === "scienceResearch" && !e.cancelled;
}
function isPendingShipBuild(e) {
    return e.eventType === "shipBuild" && !e.cancelled;
}
function isPendingShipUpgrade(e) {
    return e.eventType === "shipUpgrade" && !e.cancelled;
}
function isPendingScrapRecycle(e) {
    return e.eventType === "scrapRecycle" && !e.cancelled;
}
/**
 * Lazily replay every due checkpoint event and advance the planet to `now`.
 *
 * Resource accrual is segmented: each due event first gets a tick from the
 * cursor up to its own `endDate` (inclusive — `endDate <= now` applies),
 * then its effect runs. A buildingBuild refreshes derived stats, so the
 * following segment accrues at the new rates and clips at the new
 * capacities. There is deliberately no cap on the total elapsed time.
 *
 * A resolving scienceResearch event bumps a *local* copy of `ctx.sciences`
 * (`sciences` below) so later segments/events in THIS SAME replay already
 * see the new Physics/Ecology/Mathematics level — e.g. researching
 * Mathematics then immediately queuing a building in the same call uses the
 * post-research multiplier. This is planet-local precision only: it does
 * NOT reach the player's OTHER planets, whose own next replay picks up the
 * bump from the profile doc's `sciences` field once the functions-layer
 * caller writes it (see NOTE(scienceResearch) above `applyEvent`).
 *
 * Ordering: `(endDate, planet-before-profile, eventId)`. Planet and profile
 * eventId counters are independent, so the source tie-break keeps replays
 * deterministic when both lists hold the same endDate.
 */
export function replayEventsToNow(planet, planetEvents, profileEvents, now, ctx) {
    const sciences = { ...ctx.sciences };
    const tickCtxFor = () => ({
        sunAgeDays: ctx.sunAgeDays,
        physicsLevel: sciences.physics ?? 0,
        ecologyLevel: sciences.ecology ?? 0,
        economyMultiplier: ctx.teamPlanetOptions?.economyMultiplier,
        ozoneExempt: ctx.teamPlanetOptions?.ozoneExempt,
    });
    const accrue = (current, fromMs, to) => {
        const change = ctx.resourceMultiplierChange;
        const changeMs = change?.at.getTime();
        const tick = (p, startMs, end, multiplier) => calculatePlanetTick(p, Math.max(0, (end.getTime() - startMs) / 1000), multiplier, { ...tickCtxFor(), now: end });
        if (!change || changeMs <= fromMs)
            return tick(current, fromMs, to, ctx.resourceMultiplier);
        if (changeMs >= to.getTime())
            return tick(current, fromMs, to, change.before);
        return tick(tick(current, fromMs, change.at, change.before), changeMs, to, ctx.resourceMultiplier);
    };
    const tag = (events, source) => events
        .filter((e) => !e.cancelled && checkpointTime(e).getTime() <= now.getTime())
        .map((event) => ({ event, source }));
    const due = [...tag(planetEvents, "planet"), ...tag(profileEvents, "profile")].sort((a, b) => checkpointTime(a.event).getTime() - checkpointTime(b.event).getTime() ||
        a.event.eventId - b.event.eventId ||
        (a.source === b.source ? 0 : a.source === "planet" ? -1 : 1));
    let current = planet;
    let cursorMs = planet.state.lastTickAt.getTime();
    const applied = [];
    const appliedIds = {
        planet: new Set(),
        profile: new Set(),
    };
    for (const { event, source } of due) {
        const resolveDate = checkpointTime(event);
        current = accrue(current, cursorMs, resolveDate);
        current = applyEvent(current, event, sciences.mathematics ?? 0, sciences.architecture ?? 0, ctx.teamPlanetOptions);
        if (event.eventType === "scienceResearch") {
            sciences[event.eventDetails.scienceId] = event.eventDetails.targetLevel;
            if (event.eventDetails.scienceId === "mathematics" || event.eventDetails.scienceId === "architecture") {
                current = applyDerivedStats(current, sciences.mathematics ?? 0, sciences.architecture ?? 0, ctx.teamPlanetOptions);
            }
        }
        applied.push({ event, status: "applied", resolvedAt: resolveDate });
        appliedIds[source].add(event.eventId);
        cursorMs = Math.max(cursorMs, resolveDate.getTime());
    }
    current = accrue(current, cursorMs, now);
    const planetPending = planetEvents.filter((e) => !appliedIds.planet.has(e.eventId));
    const profilePending = profileEvents.filter((e) => !appliedIds.profile.has(e.eventId));
    return {
        planet: withProjectedNextCosts(current, planetPending, sciences.architecture ?? 0),
        planetPending,
        profilePending,
        applied,
        profileChanged: appliedIds.profile.size > 0,
    };
}
/**
 * Effect dispatch. Every `TickEventType` arm is handled below (buildingBuild,
 * scienceResearch, shipUpgrade, shipBuild, scrapRecycle) — the `default` arm
 * is unreachable for a well-typed `event` (the `never` assignment makes that
 * a compile error if a new arm is ever added without an applier), and stays
 * as a runtime guard only because stored tick-checkpoint docs aren't
 * zod-validated on read, so a malformed `eventType` from Firestore is still
 * possible.
 *
 * NOTE(scienceResearch): a research event carries no planet-local effect —
 * `Planet` has no `sciences` field, the level lives on the player profile.
 * This dispatcher (and the single-planet replay it runs inside) leaves the
 * planet unchanged for this event type; the caller in the functions layer
 * is responsible for reading the applied `scienceResearch` records back out
 * of `ReplayResult.applied` and bumping the profile's science level. Doing
 * that with full precision also requires advancing every OTHER planet of
 * the player to the event's endDate first (so their pre-bump accrual uses
 * pre-bump context) before the level flips — that cross-planet orchestration
 * lives in the functions layer, not here.
 *
 * shipUpgrade shares the exact same shape as scienceResearch: no planet-local
 * effect (upgrade levels live on the player profile, applying to every
 * planet's ships of that type), same functions-layer bump responsibility.
 */
function applyEvent(planet, event, mathematicsLevel, architectureLevel, teamPlanetOptions) {
    switch (event.eventType) {
        case "buildingBuild":
            return applyBuildingBuild(planet, event, mathematicsLevel, architectureLevel, teamPlanetOptions);
        case "scienceResearch":
            return planet;
        case "shipUpgrade":
            return planet;
        case "shipBuild":
            return applyShipBuild(planet, event);
        case "scrapRecycle":
            return applyScrapRecycle(planet, event);
        default: {
            const _exhaustive = event;
            const unexpected = _exhaustive;
            throw new Error(`No applier implemented for event type "${unexpected.eventType}" (eventId ${unexpected.eventId})`);
        }
    }
}
/**
 * Complete a building upgrade: bump the level and refresh derived stats.
 * The cost was already paid at queue time, so nothing is deducted here.
 */
function applyBuildingBuild(planet, event, mathematicsLevel, architectureLevel, teamPlanetOptions) {
    const { buildingType, targetLevel, plotId } = event.eventDetails;
    const existing = planet.buildings[buildingType];
    return applyDerivedStats({
        ...planet,
        buildings: {
            ...planet.buildings,
            [buildingType]: {
                ...existing,
                level: targetLevel,
                // A construct (targetLevel === 1) claims its plot here — an
                // upgrade of an already-built building keeps whatever plot
                // it already has (its event never carries one).
                ...(existing?.plotId ? {} : plotId ? { plotId } : {}),
            },
        },
    }, mathematicsLevel, architectureLevel, teamPlanetOptions);
}
/**
 * Complete a ship batch: dock `quantity` units of `shipType`. The cost was
 * paid at queue time. No derived-stats refresh is needed — the one stat
 * ships influence (solar-satellite energy income) is recomputed live from
 * `planet.ships` by every tick segment.
 */
function applyShipBuild(planet, event) {
    const { shipType, quantity } = event.eventDetails;
    if (isTeamPlanet(planet)) {
        const race = teamShipBuildRace(event);
        return teamPlanetDockShips(planet, race, shipType, quantity);
    }
    return dockShips(planet, shipType, quantity);
}
/**
 * Complete a recycle batch: dock every ship in the batch. The scrap was
 * already deducted at queue time, as were the batch's hangar slots — since
 * issue #119 `planScrapRecycleQueueEntry` reserves them, so a batch queued
 * within capacity still fits on arrival. Docking never fails regardless: if
 * the planet is over its Launcher-derived population capacity by then (a
 * Station transfer landing in the meantime, say), the reactive
 * overpopulation lock (`isPlanetOverpopulated`) simply engages on the next
 * read (issue #21: "if it causes an overpopulation, the planet will be
 * locked").
 *
 * Called from two places: `applyEvent` dispatch, once replay reaches the
 * batch's `deliverAt` (the automatic 48-hour hold — lazy replay guarantees
 * this runs on the first tick/action after that point, however long that
 * is, since there is no cap on elapsed time; see `replayEventsToNow`); and
 * `deliverScrapRecycleBatch`, which calls this directly for an on-demand
 * delivery requested any time from `endDate` onward, ahead of that deadline.
 */
function applyScrapRecycle(planet, event) {
    let docked = planet;
    for (const [key, quantity] of Object.entries(event.eventDetails.ships)) {
        if (!quantity)
            continue;
        docked = dockShips(docked, Number(key), quantity);
    }
    return docked;
}
function dockShips(planet, shipType, quantity) {
    if (quantity <= 0)
        return planet;
    return {
        ...planet,
        ships: {
            ...planet.ships,
            [shipType]: (planet.ships?.[shipType] ?? 0) + quantity,
        },
    };
}
/** Remove docked ships (Axon mutation donors). Callers validate availability first. */
function undockShips(planet, shipType, quantity) {
    if (quantity <= 0)
        return planet;
    return {
        ...planet,
        ships: {
            ...planet.ships,
            [shipType]: (planet.ships?.[shipType] ?? 0) - quantity,
        },
    };
}
// ---------------------------------------------------------------------------
// Queueing
// ---------------------------------------------------------------------------
/**
 * Validate and stage a building upgrade on an already-replayed planet.
 *
 * - Queue limit counts pending buildingBuild events planet-wide.
 * - Cost and max-level use the projected level (current + queued).
 * - Prerequisites use ACTUAL levels and sciences only — queued upgrades
 *   never satisfy a requirement.
 * - The full cost is paid immediately (energy goes to `consumed`).
 * - Zone FIFO: construction starts when the zone's last pending item ends.
 */
/**
 * Resolve and validate the plot a level 0→1 construct claims (DS-28). All
 * of it runs server-side — the client cannot be trusted to only ever offer
 * a legal plot.
 *
 * - A building that already carries a `plotId` (built once, then destroyed
 *   back to level 0) always reconstructs on that same plot — the submitted
 *   `plotId` is ignored rather than rejected, since the client has no way
 *   to know a "level 0" building might already own a plot.
 * - Otherwise a `plotId` is required, must name a real plot
 *   (`SURFACE_PLOT_IDS`), in the building's own zone, not already occupied
 *   by another building (built, or previously built and destroyed — either
 *   way it still carries that `plotId`), and not claimed by another
 *   pending construct.
 */
function resolveConstructPlotId(planet, pending, buildingType, zone, plotId) {
    const existingPlotId = planet.buildings[buildingType]?.plotId;
    if (existingPlotId)
        return existingPlotId;
    const claimed = claimedPlotIds(planet, pending, buildingType);
    // No plot chosen: place it on the zone's first free plot. Only the
    // surface has a plot picker — the zone list and the alliance Team Planet
    // panel construct straight from a list — so requiring every caller to
    // supply one makes any screen that forgets fail at the server, which is
    // exactly how the Team Planet build queue broke.
    if (!plotId) {
        const free = SURFACE_PLOT_IDS[zone].find((id) => !claimed.has(id));
        if (!free) {
            throw new QueueError("failed-precondition", `Every plot in the ${zone} zone is taken`);
        }
        return free;
    }
    if (!SURFACE_PLOT_IDS[zone].includes(plotId)) {
        throw new QueueError("failed-precondition", `${plotId} is not a valid plot`);
    }
    if (claimed.has(plotId)) {
        throw new QueueError("failed-precondition", `Plot ${plotId} is already occupied`);
    }
    return plotId;
}
/** Plots already spoken for on this planet — held by a built building, or claimed by a queued construct. */
function claimedPlotIds(planet, pending, buildingType) {
    const claimed = new Set();
    for (const type of Object.keys(planet.buildings)) {
        if (type === buildingType)
            continue;
        const id = planet.buildings[type]?.plotId;
        if (id)
            claimed.add(id);
    }
    for (const event of pending.filter(isPendingBuild)) {
        const id = event.eventDetails.plotId;
        if (id)
            claimed.add(id);
    }
    return claimed;
}
export function planBuildingQueueEntry(planet, pending, buildingType, sciences, now, speedMultiplier, queueLimit, nextEventId, 
/**
 * Chosen plot (DS-28) — only meaningful, and only validated, for a
 * level 0→1 construct. Ignored for an upgrade of an already-built
 * building (it keeps its existing plot). Required for a fresh
 * construct unless the building already carries a `plotId` from a
 * previous build-then-destroy, in which case that plot is reused and
 * this argument is ignored.
 */
plotId) {
    const def = BUILDING_DEFINITIONS[buildingType];
    const builds = pending.filter(isPendingBuild);
    if (builds.length >= queueLimit) {
        throw new QueueError("queue-full", `Build queue is full (${queueLimit} slots)`);
    }
    const projectedLevel = computeProjectedBuildingLevels(planet, pending)[buildingType];
    if (def.maxLevel > 0 && projectedLevel >= def.maxLevel) {
        throw new QueueError("failed-precondition", `${def.name} is already at max level (counting queued upgrades)`);
    }
    const actualLevels = extractBuildingLevels(planet.buildings);
    if (!checkPrerequisites(buildingType, actualLevels, sciences)) {
        throw new QueueError("failed-precondition", `Prerequisites for ${def.name} are not met`);
    }
    // `projectedLevel`, not the actual level, decides whether THIS queue
    // entry is the 0→1 construct: a second queued build for a still-level-0
    // building (one construct already pending) is really an upgrade of the
    // first queued level, not a second construct needing its own plot.
    const isConstruct = projectedLevel === 0;
    const resolvedPlotId = isConstruct
        ? resolveConstructPlotId(planet, pending, buildingType, def.zone, plotId)
        : undefined;
    const cost = calculateBuildingUpgradeCost(buildingType, projectedLevel, sciences.architecture ?? 0);
    const affordable = canAffordCost(cost, {
        metal: planet.resources.metal.current,
        minerals: planet.resources.minerals.current,
        gas: planet.resources.gas.current,
        energy: planet.resources.energy.current,
        ozone: planet.state.ozone.current,
    });
    if (!affordable) {
        throw new QueueError("insufficient-resources", `Insufficient resources for ${def.name}`);
    }
    const zoneTailMs = builds
        .filter((e) => e.eventDetails.zone === def.zone)
        .reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    const startMs = Math.max(now.getTime(), zoneTailMs);
    const durationSeconds = computeBuildDurationSeconds(buildingType, projectedLevel + 1, actualLevels.robotFactory, speedMultiplier);
    const event = {
        eventId: nextEventId,
        eventType: "buildingBuild",
        createdDate: now,
        updatedDate: now,
        endDate: new Date(startMs + durationSeconds * 1000),
        eventDetails: {
            buildingType,
            targetLevel: projectedLevel + 1,
            zone: def.zone,
            paidCost: cost,
            durationSeconds,
            startDate: new Date(startMs),
            ...(resolvedPlotId ? { plotId: resolvedPlotId } : {}),
        },
    };
    const newPending = [...pending, event];
    return {
        planet: withProjectedNextCosts(applyResourceCost(planet, cost, now), newPending, sciences.architecture ?? 0),
        pending: newPending,
        event,
        nextEventId: nextEventId + 1,
    };
}
/**
 * Queue as many consecutive levels of one building as fit right now — the
 * Platinum "Queue max" action (issue #219). Runs {@link planBuildingQueueEntry}
 * repeatedly until it refuses, so every level gets exactly the same checks,
 * price, chaining and plot handling as a single upgrade, and each level still
 * takes its own queue slot. Never throws a `QueueError`: a request where
 * nothing fits comes back with `count: 0` and the reason in `limitedBy`.
 * The client runs the same function for its preview, so the preview matches
 * what the server will queue. `maxCount` caps the run at the count the
 * player was shown, so state that grew between preview and click never
 * spends more than the button promised (`limitedBy: "requested"`).
 */
export function planBuildingQueueMax(planet, pending, buildingType, sciences, now, speedMultiplier, queueLimit, nextEventId, plotId, maxCount) {
    let current = { planet, pending, nextEventId };
    const events = [];
    while (maxCount === undefined || events.length < maxCount) {
        try {
            const queued = planBuildingQueueEntry(current.planet, current.pending, buildingType, sciences, now, speedMultiplier, queueLimit, current.nextEventId, events.length === 0 ? plotId : undefined);
            events.push(queued.event);
            current = { planet: queued.planet, pending: queued.pending, nextEventId: queued.nextEventId };
        }
        catch (e) {
            if (!(e instanceof QueueError))
                throw e;
            break;
        }
    }
    return {
        ...current,
        events,
        count: events.length,
        totalCost: sumResourceCosts(events.map((event) => event.eventDetails.paidCost)),
        limitedBy: maxCount !== undefined && events.length >= maxCount
            ? "requested"
            : queueMaxLimit(current.planet, current.pending, buildingType, sciences, queueLimit),
    };
}
/**
 * What cancelling `eventId` would do, without doing it: the levels the
 * cascade removes (the target and every higher queued level of the same
 * building, lowest first) and the resources the planet would actually be
 * credited — after `cancelBuildingEvent`'s storage cap — next to the
 * nominal 75% refund. Lets a confirm dialog name exactly what is lost.
 */
export function previewBuildingCancel(planet, pending, eventId, now) {
    const result = cancelBuildingEvent(planet, pending, eventId, now);
    const cancelled = result.cancelled
        .map((record) => record.event)
        .filter(isPendingBuildEventLike)
        .sort((a, b) => a.eventDetails.targetLevel - b.eventDetails.targetLevel);
    const creditedRefund = {};
    for (const key of ["metal", "minerals", "gas"]) {
        const credited = Math.floor(result.planet.resources[key].current) - Math.floor(planet.resources[key].current);
        if (credited > 0)
            creditedRefund[key] = credited;
    }
    const energy = (planet.resources.energy.consumed ?? 0) - (result.planet.resources.energy.consumed ?? 0);
    if (energy > 0)
        creditedRefund.energy = energy;
    return {
        levels: cancelled.map((e) => e.eventDetails.targetLevel),
        nominalRefund: sumResourceCosts(result.cancelled.map((record) => record.refund ?? {})),
        creditedRefund,
    };
}
function isPendingBuildEventLike(e) {
    return e.eventType === "buildingBuild";
}
/** Add several costs key by key; keys that sum to zero are left out. */
export function sumResourceCosts(costs) {
    const total = {};
    for (const cost of costs) {
        for (const [key, amount] of Object.entries(cost)) {
            if (amount)
                total[key] = (total[key] ?? 0) + amount;
        }
    }
    return total;
}
function queueMaxLimit(planet, pending, buildingType, sciences, queueLimit) {
    const def = BUILDING_DEFINITIONS[buildingType];
    if (pending.filter(isPendingBuild).length >= queueLimit)
        return "queue";
    const projectedLevel = computeProjectedBuildingLevels(planet, pending)[buildingType];
    if (def.maxLevel > 0 && projectedLevel >= def.maxLevel)
        return "max-level";
    if (!checkPrerequisites(buildingType, extractBuildingLevels(planet.buildings), sciences))
        return "prerequisites";
    const cost = calculateBuildingUpgradeCost(buildingType, projectedLevel, sciences.architecture ?? 0);
    for (const key of ["metal", "minerals", "gas"]) {
        if ((cost[key] ?? 0) > planet.resources[key].current)
            return key;
    }
    if ((cost.energy ?? 0) > 0 && (cost.energy ?? 0) > planet.resources.energy.current)
        return "energy";
    return "plot";
}
// ---------------------------------------------------------------------------
// Cancellation
// ---------------------------------------------------------------------------
/**
 * Cancel a pending buildingBuild event on an already-replayed planet.
 *
 * The target and every queued higher level of the same building are
 * cancelled (level continuity), each refunded at 75%. Prerequisites need no
 * re-check: they were validated against actual levels, which cancelling
 * cannot lower. The affected zone queue then reflows — items already under
 * construction keep their schedule; not-yet-started items shift to
 * `start = max(now, previous end)`.
 */
export function cancelBuildingEvent(planet, pending, eventId, now, sciences = {}) {
    const target = pending.find((e) => e.eventId === eventId && isPendingBuild(e));
    if (!target || !isPendingBuild(target)) {
        throw new QueueError("event-not-found", `No pending build with eventId ${eventId}`);
    }
    const cascade = pending
        .filter(isPendingBuild)
        .filter((e) => e.eventId !== target.eventId &&
        e.eventDetails.buildingType === target.eventDetails.buildingType &&
        e.eventDetails.targetLevel > target.eventDetails.targetLevel)
        .sort(byEndDateThenId);
    let current = planet;
    const cancelled = [];
    for (const e of [target, ...cascade]) {
        current = applyResourceRefund(current, e.eventDetails.paidCost, "building", now);
        cancelled.push({
            event: { ...e, cancelled: true, updatedDate: now },
            status: "cancelled",
            resolvedAt: now,
            refund: calculateNominalRefund(e.eventDetails.paidCost, "building"),
        });
    }
    const removedIds = new Set(cancelled.map((r) => r.event.eventId));
    const survivors = pending.filter((e) => !removedIds.has(e.eventId));
    const newPending = reflowZoneQueues(survivors, now);
    return {
        planet: withProjectedNextCosts(current, newPending, sciences.architecture ?? 0),
        pending: newPending,
        cancelled,
    };
}
/**
 * Cancel every pending buildingBuild event for `buildingType`, regardless of
 * target level. Used by building destruction, which must clear the whole
 * queue before removing a level — a queued upgrade always assumes level
 * continuity from whatever level currently exists. Each cancelled event
 * refunds metal/minerals/gas at the normal 75% building rate; Energy
 * refunds in full — see {@link destroyBuildingLevel}. Unlike
 * {@link cancelBuildingEvent} this needs no target eventId or cascade
 * filter: every pending event for the building is, by construction, part
 * of the same sequential chain.
 */
function cancelAllPendingForBuilding(planet, pending, buildingType, now) {
    const targets = pending
        .filter(isPendingBuild)
        .filter((e) => e.eventDetails.buildingType === buildingType)
        .sort(byEndDateThenId);
    let current = planet;
    const cancelled = [];
    for (const e of targets) {
        current = applyResourceRefundAtRates(current, e.eventDetails.paidCost, REFUND_RATES.building, 1, now);
        cancelled.push({
            event: { ...e, cancelled: true, updatedDate: now },
            status: "cancelled",
            resolvedAt: now,
            refund: calculateNominalRefundAtRates(e.eventDetails.paidCost, REFUND_RATES.building, 1),
        });
    }
    const removedIds = new Set(cancelled.map((r) => r.event.eventId));
    const survivors = pending.filter((e) => !removedIds.has(e.eventId));
    const newPending = reflowZoneQueues(survivors, now);
    return { planet: current, pending: newPending, cancelled };
}
/**
 * Destroy one level of `buildingType` on an already-replayed planet.
 *
 * Any pending buildingBuild events for this building are cancelled first,
 * since a queued upgrade assumes continuity from the level about to be
 * removed. The destroyed level itself refunds metal/minerals/gas at 75% of
 * the FULL cost to reach it, priced at `architectureLevel = 0`
 * regardless of the player's actual science level — destruction refunds
 * are irrespective of whatever discount the player originally paid.
 * Energy refunds in FULL everywhere in this function (both the cancelled
 * queue entries and the destroyed level itself): unlike metal/minerals/gas,
 * which are materials invested into the structure and only partially
 * recoverable, Energy is a live draw tied to the level's existence — once
 * the level no longer exists (queued and cancelled, or built and
 * destroyed) the draw simply stops, so there is nothing partial to keep
 * back. Derived stats (rates/capacities/population/nextCosts) are
 * refreshed afterward for every building, since a level change here can
 * move storage capacity, production, and dependent buildings' next-cost
 * pricing.
 */
export function destroyBuildingLevel(planet, pending, buildingType, sciences, now) {
    const currentLevel = planet.buildings[buildingType]?.level ?? 0;
    if (currentLevel <= 0) {
        throw new QueueError("failed-precondition", `${BUILDING_DEFINITIONS[buildingType].name} has no level to destroy`);
    }
    const { planet: afterCancel, pending: newPending, cancelled, } = cancelAllPendingForBuilding(planet, pending, buildingType, now);
    const fullCost = calculateBuildingUpgradeCost(buildingType, currentLevel - 1, 0);
    const refunded = applyResourceRefundAtRates(afterCancel, fullCost, REFUND_RATES.building, 1, now);
    const withLevelDown = {
        ...refunded,
        buildings: {
            ...refunded.buildings,
            [buildingType]: { ...refunded.buildings[buildingType], level: currentLevel - 1 },
        },
    };
    const derived = applyDerivedStats(withLevelDown, sciences.mathematics ?? 0, sciences.architecture ?? 0);
    const finalPlanet = withProjectedNextCosts(derived, newPending, sciences.architecture ?? 0);
    return { planet: finalPlanet, pending: newPending, cancelled };
}
/**
 * Recompute start/end dates of pending builds per zone after a removal.
 * Started items are immutable anchors; each later item starts when its
 * predecessor ends (or `now`, whichever is later).
 */
function reflowZoneQueues(pending, now) {
    const builds = pending.filter(isPendingBuild);
    const others = pending.filter((e) => !isPendingBuild(e));
    const reflowed = [];
    for (const zone of ZONES) {
        let cursorMs = now.getTime();
        const zoneBuilds = builds
            .filter((e) => e.eventDetails.zone === zone)
            .sort(byEndDateThenId);
        for (const e of zoneBuilds) {
            if (e.eventDetails.startDate.getTime() <= now.getTime()) {
                reflowed.push(e);
                cursorMs = Math.max(cursorMs, e.endDate.getTime());
            }
            else if (e.eventDetails.startDate.getTime() === cursorMs) {
                reflowed.push(e);
                cursorMs = e.endDate.getTime();
            }
            else {
                const endMs = cursorMs + e.eventDetails.durationSeconds * 1000;
                reflowed.push({
                    ...e,
                    updatedDate: now,
                    endDate: new Date(endMs),
                    eventDetails: { ...e.eventDetails, startDate: new Date(cursorMs) },
                });
                cursorMs = endMs;
            }
        }
    }
    return [...reflowed, ...others];
}
// ---------------------------------------------------------------------------
// Ship queueing — per-lane sequential FIFOs per planet (`queueLane`, issue
// #91). Most races only ever use the `default` lane, i.e. one shipyard line
// exactly like before this feature; Axons additionally split into
// `creation`/`mutation`. Lanes run independently but share one population
// pool — there is no queue-slot limit for ships of any lane, construction is
// bounded by free population capacity instead. Robots additionally get
// `planShipBuildGroupQueueEntry` below, an orthogonal mechanic (batched
// orders + held delivery) that still resolves within the `default` lane.
// ---------------------------------------------------------------------------
function isSameLane(e, lane) {
    return (e.eventDetails.queueLane ?? "default") === lane;
}
/**
 * Validate and stage a single ship batch on an already-replayed planet.
 * Shared by {@link planShipBuildQueueEntry} (one item) and
 * {@link planShipBuildGroupQueueEntry} (multiple items chained together).
 *
 * - Requirements (Factory level, sciences) use ACTUAL levels only.
 * - Axon mutation: `mutatesFrom.quantity × quantity` docked donor ships are
 *   required and consumed IMMEDIATELY ("the required ship mutates into the
 *   new one and is lost during the process" — ships.md), so their hangar
 *   slots free up before the population check below.
 * - Population: `quantity × hangar` must fit in free capacity (capacity −
 *   docked-after-donors − already-queued, across every lane AND every
 *   pending Recovery Center batch, which docks ships too — issue #119). No
 *   queue-slot limit.
 * - The full batch cost is paid immediately (ships never cost energy).
 * - Per-lane FIFO: the batch starts when the last pending batch IN THE SAME
 *   LANE ends — `computeShipBuildLane(race, def)` selects the lane.
 */
export function stageShipBuildItem(planet, pending, race, shipId, quantity, sciences, now, speedMultiplier, nextEventId, 
/** Team-planet-only: overrides `computeShipBuildLane`'s race/mutation-derived lane with a fixed one (per-race FIFOs — `TEAM_PLANET_SHIP_BUILD_LANES`). */
laneOverride, 
/** Team-planet-only: stamped on the event so the leader may cancel it regardless of who queued it, while everyone else may only cancel their own. */
queuedByUserId) {
    const def = getShipDefinition(race, shipId);
    if (!Number.isInteger(quantity) || quantity < 1) {
        throw new QueueError("failed-precondition", "Ship quantity must be a positive integer");
    }
    const actualLevels = extractBuildingLevels(planet.buildings);
    if (!checkShipRequirements(race, shipId, actualLevels, sciences)) {
        throw new QueueError("failed-precondition", `Prerequisites for ${def.name} are not met`);
    }
    const mutation = def.mutatesFrom;
    const donorsNeeded = mutation ? mutation.quantity * quantity : 0;
    const isTeam = isTeamPlanet(planet);
    if (mutation) {
        const docked = isTeam
            ? (planet.teamShips?.[race]?.docked[mutation.shipId] ?? 0)
            : (planet.ships?.[mutation.shipId] ?? 0);
        if (docked < donorsNeeded) {
            const donorDef = getShipDefinition(race, mutation.shipId);
            throw new QueueError("failed-precondition", `${quantity} × ${def.name} requires ${donorsNeeded} docked ${donorDef.name} (${docked} available)`);
        }
    }
    const afterDonors = mutation
        ? (isTeam
            ? teamPlanetUndockShips(planet, race, mutation.shipId, donorsNeeded)
            : undockShips(planet, mutation.shipId, donorsNeeded))
        : planet;
    const freePopulation = isTeam
        ? calculateTeamPlanetFreePopulation(afterDonors, pending)
        : calculateFreePopulation(race, afterDonors.resources.population.capacity, afterDonors.ships, pending, afterDonors.shipsInFlight);
    const neededPopulation = def.hangar * quantity;
    if (neededPopulation > freePopulation) {
        throw new QueueError("insufficient-population", `Not enough free population for ${quantity} × ${def.name} (needs ${neededPopulation}, ${Math.max(0, freePopulation)} free)`);
    }
    const cost = calculateShipBuildCost(race, shipId, quantity);
    const affordable = canAffordCost(cost, {
        metal: planet.resources.metal.current,
        minerals: planet.resources.minerals.current,
        gas: planet.resources.gas.current,
        energy: planet.resources.energy.current,
        ozone: planet.state.ozone.current,
    });
    if (!affordable) {
        throw new QueueError("insufficient-resources", `Insufficient resources for ${quantity} × ${def.name}`);
    }
    const lane = laneOverride ?? computeShipBuildLane(race, def);
    const queueTailMs = pending
        .filter(isPendingShipBuild)
        .filter((e) => isSameLane(e, lane))
        .reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    const startMs = Math.max(now.getTime(), queueTailMs);
    const { perUnitSeconds, totalSeconds } = calculateShipBuildTime(race, shipId, quantity, actualLevels.shipyard, actualLevels.automationCenter, speedMultiplier);
    const event = {
        eventId: nextEventId,
        eventType: "shipBuild",
        createdDate: now,
        updatedDate: now,
        endDate: new Date(startMs + totalSeconds * 1000),
        eventDetails: {
            shipType: shipId,
            quantity,
            paidCost: cost,
            perUnitSeconds,
            durationSeconds: totalSeconds,
            startDate: new Date(startMs),
            queueLane: lane,
            ...(mutation ? { consumedShips: { shipId: mutation.shipId, quantity: donorsNeeded } } : {}),
            ...(queuedByUserId ? { queuedByUserId } : {}),
        },
    };
    return {
        planet: applyResourceCost(afterDonors, cost, now),
        pending: [...pending, event],
        event,
        nextEventId: nextEventId + 1,
    };
}
/** Validate and stage a single-item ship batch — see {@link stageShipBuildItem}. */
export function planShipBuildQueueEntry(planet, pending, race, shipId, quantity, sciences, now, speedMultiplier, nextEventId) {
    return stageShipBuildItem(planet, pending, race, shipId, quantity, sciences, now, speedMultiplier, nextEventId);
}
/**
 * Robots-only (issue #91): queue multiple ship-build orders as one linked
 * group whose combined delivery is held back — instead of docking
 * automatically the moment each item's construction finishes — until
 * `AUTO_DELIVERY_HOLD_HOURS` after the LAST item in the group completes.
 * Each item still validates/pays/staged exactly like
 * {@link stageShipBuildItem} (same requirements, population, and cost
 * checks), chained sequentially in the `default` lane; only after every item
 * stages successfully does this function patch all of them with the shared
 * `groupId`/`deliverAt`. The player may also request delivery on demand any
 * time from the group's completion onward, before that automatic deadline —
 * see {@link deliverShipBuildGroup}.
 *
 * The race gate ("only Robots may call this") is enforced by the caller
 * (Cloud Function), not here — this function itself is race-agnostic, same
 * as `stageShipBuildItem`.
 */
export function planShipBuildGroupQueueEntry(planet, pending, race, items, sciences, now, speedMultiplier, nextEventId) {
    const entries = Object.entries(items)
        .filter((entry) => !!entry[1]);
    if (entries.length < SHIP_GROUP_MIN_ITEMS || entries.length > SHIP_GROUP_MAX_ITEMS) {
        throw new QueueError("failed-precondition", `A ship build group must contain between ${SHIP_GROUP_MIN_ITEMS} and ${SHIP_GROUP_MAX_ITEMS} distinct ship types (got ${entries.length})`);
    }
    for (const [, quantity] of entries) {
        if (!Number.isInteger(quantity) || quantity < 1) {
            throw new QueueError("failed-precondition", "Ship quantities must be positive integers");
        }
    }
    let currentPlanet = planet;
    let currentPending = pending;
    let currentNextEventId = nextEventId;
    const stagedIds = [];
    for (const [shipIdKey, quantity] of entries) {
        const staged = stageShipBuildItem(currentPlanet, currentPending, race, Number(shipIdKey), quantity, sciences, now, speedMultiplier, currentNextEventId);
        currentPlanet = staged.planet;
        currentPending = staged.pending;
        currentNextEventId = staged.nextEventId;
        stagedIds.push(staged.event.eventId);
    }
    const groupId = stagedIds[0];
    const stagedIdSet = new Set(stagedIds);
    const groupCompletionMs = currentPending
        .filter((e) => e.eventType === "shipBuild" && stagedIdSet.has(e.eventId))
        .reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    const deliverAt = new Date(groupCompletionMs + AUTO_DELIVERY_HOLD_HOURS * 3600000);
    const patchedPending = currentPending.map((e) => e.eventType === "shipBuild" && stagedIdSet.has(e.eventId)
        ? { ...e, eventDetails: { ...e.eventDetails, groupId, deliverAt } }
        : e);
    const events = patchedPending.filter((e) => e.eventType === "shipBuild" && stagedIdSet.has(e.eventId));
    return {
        planet: currentPlanet,
        pending: patchedPending,
        events,
        nextEventId: currentNextEventId,
    };
}
/**
 * Deliver a Robots ship-build group on demand, before its automatic
 * `AUTO_DELIVERY_HOLD_HOURS` deadline (issue #91). Docks every batch in the
 * group immediately, but only once construction has actually finished for
 * ALL of them — i.e. `now` is at or past the LATEST `endDate` among the
 * group's batches, the same instant `deliverAt` was originally computed
 * from. Requesting delivery while any item is still mid-construction is
 * rejected with `failed-precondition`; an unknown `groupId` (or one that
 * `replayEventsToNow` has already auto-delivered past `deliverAt`) is
 * rejected with `event-not-found`.
 */
export function deliverShipBuildGroup(planet, pending, groupId, now) {
    const members = pending.filter((e) => isPendingShipBuild(e) && e.eventDetails.groupId === groupId);
    if (members.length === 0) {
        throw new QueueError("event-not-found", `No pending ship build group with groupId ${groupId}`);
    }
    const readyAtMs = members.reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    if (now.getTime() < readyAtMs) {
        throw new QueueError("failed-precondition", "This group has not finished construction yet");
    }
    let current = planet;
    const delivered = [];
    for (const e of members) {
        current = applyShipBuild(current, e);
        delivered.push({ event: e, status: "applied", resolvedAt: now });
    }
    const deliveredIds = new Set(members.map((e) => e.eventId));
    const newPending = pending.filter((e) => !deliveredIds.has(e.eventId));
    return { planet: current, pending: newPending, delivered };
}
/**
 * Units of a pending shipBuild batch whose per-unit slot has already
 * elapsed at `now`. On cancellation these are delivered to the hangar
 * rather than refunded. Always strictly fewer than the batch `quantity`: a
 * batch whose final unit is due would have completed during replay.
 *
 * Shared by {@link cancelShipBuildEvent} and
 * {@link calculateShipBuildCancellationPopulation} so a cancellation and
 * the preview of its cost can never disagree.
 */
export function calculateDeliveredShipBuildUnits(details, now) {
    const elapsedMs = now.getTime() - details.startDate.getTime();
    return Math.min(Math.max(0, Math.floor(elapsedMs / (details.perUnitSeconds * 1000))), details.quantity - 1);
}
/**
 * What cancelling a pending shipBuild batch would do to a planet's
 * population, without mutating anything — the preview behind the population
 * lock's queue panel.
 *
 * `dockedAdded` is never negative: cancelling delivers the units the
 * shipyard already finished, and hands back the donor ships an Axon
 * mutation consumed for the units that now never will be. Cancelling a
 * build therefore CANNOT clear an overpopulation lock — that lock counts
 * docked ships only ({@link isPlanetOverpopulated}) — and on a started
 * batch it pushes docked population UP. What a cancellation does lower is
 * the planet's used population (docked + in flight + queued), and the docked total the
 * planet is heading for once the batch lands.
 */
export function calculateShipBuildCancellationPopulation(race, event, now) {
    const { shipType, quantity, consumedShips } = event.eventDetails;
    const hangar = getShipDefinition(race, shipType).hangar;
    const deliveredUnits = calculateDeliveredShipBuildUnits(event.eventDetails, now);
    const unfinishedUnits = quantity - deliveredUnits;
    let dockedAdded = deliveredUnits * hangar;
    if (consumedShips) {
        // consumedShips.quantity is donors-per-unit × quantity by construction,
        // matching cancelShipBuildEvent's own donor arithmetic.
        const donorsPerUnit = consumedShips.quantity / quantity;
        dockedAdded +=
            Math.round(donorsPerUnit * unfinishedUnits) *
                getShipDefinition(race, consumedShips.shipId).hangar;
    }
    return {
        queuedReleased: quantity * hangar,
        dockedAdded,
        deliveredUnits,
        dockedOnCompletion: quantity * hangar,
    };
}
/**
 * Cancel a pending shipBuild batch on an already-replayed planet.
 *
 * Units whose per-unit build slot has already elapsed (`startDate +
 * k × perUnitSeconds <= now`) are delivered to `planet.ships` — the player
 * keeps what the shipyard actually finished. The unfinished remainder
 * refunds at 40–60% of its share of the paid cost (ships.md), where the
 * exact rate comes from `refundRoll` (0..1, uniformly mapped into the
 * range) — the roll is injected so this stays deterministic; the functions
 * layer passes real randomness. Axon mutation donors consumed for the
 * UNFINISHED units return in full — like Energy on building destruction,
 * they were never actually transformed, so there is nothing partial to
 * keep back; donors of delivered units stay consumed. Later batches in the
 * shipyard FIFO reflow forward. Batches are independent, so there is no
 * cascade.
 */
export function cancelShipBuildEvent(planet, pending, eventId, now, refundRoll) {
    const target = pending.find((e) => e.eventId === eventId && isPendingShipBuild(e));
    if (!target || !isPendingShipBuild(target)) {
        throw new QueueError("event-not-found", `No pending ship build with eventId ${eventId}`);
    }
    // Robots-only (issue #91): a grouped batch stays pending past its own
    // endDate — construction is done, it's just holding for `deliverAt` — so
    // it can no longer be cancelled once that point is reached.
    if (target.eventDetails.deliverAt && now.getTime() >= target.endDate.getTime()) {
        throw new QueueError("failed-precondition", "This batch has already finished construction and is awaiting delivery — it can no longer be cancelled");
    }
    const { shipType, quantity, paidCost, consumedShips } = target.eventDetails;
    const deliveredUnits = calculateDeliveredShipBuildUnits(target.eventDetails, now);
    const unfinishedUnits = quantity - deliveredUnits;
    const clampedRoll = Math.min(1, Math.max(0, refundRoll));
    const refundRate = SHIP_BUILD_REFUND_MIN + clampedRoll * (SHIP_BUILD_REFUND_MAX - SHIP_BUILD_REFUND_MIN);
    const remainingFraction = unfinishedUnits / quantity;
    const remainingCost = {};
    for (const key of ["metal", "minerals", "gas"]) {
        const paid = paidCost[key];
        if (paid)
            remainingCost[key] = Math.round(paid * remainingFraction);
    }
    const isTeam = isTeamPlanet(planet);
    const race = isTeam ? teamShipBuildRace(target) : undefined;
    let delivered = isTeam && race
        ? teamPlanetDockShips(planet, race, shipType, deliveredUnits)
        : dockShips(planet, shipType, deliveredUnits);
    if (consumedShips) {
        const donorsPerUnit = consumedShips.quantity / quantity;
        delivered = isTeam && race
            ? teamPlanetDockShips(delivered, race, consumedShips.shipId, Math.round(donorsPerUnit * unfinishedUnits))
            : dockShips(delivered, consumedShips.shipId, Math.round(donorsPerUnit * unfinishedUnits));
    }
    const refunded = applyResourceRefundAtRates(delivered, remainingCost, refundRate, 1, now);
    const cancelled = [{
            event: { ...target, cancelled: true, updatedDate: now },
            status: "cancelled",
            resolvedAt: now,
            refund: calculateNominalRefundAtRates(remainingCost, refundRate, 1),
        }];
    const survivors = pending.filter((e) => e.eventId !== target.eventId);
    const newPending = reflowShipQueue(survivors, now);
    return { planet: refunded, pending: newPending, cancelled, deliveredUnits };
}
/**
 * Recompute start/end dates of each shipyard lane's FIFO after a removal,
 * independently per lane — mirrors {@link reflowZoneQueues}. Started
 * batches are immutable anchors; each later batch in the SAME lane starts
 * when its predecessor ends (or `now`, whichever is later). A lane's
 * reflow never touches another lane's schedule.
 *
 * Lanes are derived from the events themselves rather than a fixed list, so
 * an event can never be dropped — and thus silently deleted along with the
 * resources paid for it — by carrying a lane the list forgot to mention.
 */
function reflowShipQueue(pending, now) {
    const shipBuilds = pending.filter(isPendingShipBuild);
    const others = pending.filter((e) => !isPendingShipBuild(e));
    const lanes = [];
    for (const e of shipBuilds) {
        const lane = e.eventDetails.queueLane ?? "default";
        if (!lanes.includes(lane))
            lanes.push(lane);
    }
    const reflowed = [];
    for (const lane of lanes) {
        let cursorMs = now.getTime();
        const laneBuilds = shipBuilds.filter((e) => isSameLane(e, lane)).sort(byEndDateThenId);
        for (const e of laneBuilds) {
            if (e.eventDetails.startDate.getTime() <= now.getTime()) {
                reflowed.push(e);
                cursorMs = Math.max(cursorMs, e.endDate.getTime());
            }
            else if (e.eventDetails.startDate.getTime() === cursorMs) {
                reflowed.push(e);
                cursorMs = e.endDate.getTime();
            }
            else {
                const endMs = cursorMs + e.eventDetails.durationSeconds * 1000;
                reflowed.push({
                    ...e,
                    updatedDate: now,
                    endDate: new Date(endMs),
                    eventDetails: { ...e.eventDetails, startDate: new Date(cursorMs) },
                });
                cursorMs = endMs;
            }
        }
    }
    return [...reflowed, ...others];
}
// ---------------------------------------------------------------------------
// Recovery Center queueing — PARALLEL slots (one concurrent batch per plant
// level), unlike every FIFO queue above. There is no queue-tail chaining and
// no reflow: removing one batch (cancellation) just frees its slot for the
// next `planScrapRecycleQueueEntry` call, without shifting any other
// batch's schedule.
// ---------------------------------------------------------------------------
/**
 * Validate and stage a scrap-recycle batch on an already-replayed planet.
 *
 * - Requires a built Recovery Center; its level both prices the batch
 *   (`recycleScrapRequired` — lower level costs more scrap for the same
 *   ships) and caps concurrent batches (one slot per level).
 * - Ship requirements (Factory level, sciences) use ACTUAL levels only,
 *   same as `planShipBuildQueueEntry` — recycling doesn't bypass what the
 *   shipyard itself would require to build the ship.
 * - Anti-exploit cap: at most `SCRAP_RECYCLE_DAILY_LIMIT` batches per
 *   rolling 24h window (`planet.scrapRecycleDaily`), independent of the
 *   slot limit — the window resets lazily the first time this is called
 *   after it expires.
 * - Population: like `planShipBuildQueueEntry`, `quantity × hangar` summed
 *   across the selection must fit in free capacity (capacity − docked −
 *   queued shipBuild population − ships in flight − OTHER pending recycle
 *   batches' population, since a plant with multiple slots can have several
 *   batches racing for the same capacity). This closes issue #119: recycling
 *   used to be able to complete into overpopulation from queue time already
 *   (see `applyScrapRecycle`'s comment for the remaining, still-deliberate
 *   overpopulation path — a ship returning from a flight while docked ships
 *   are already at capacity).
 * - Scrap is deducted immediately; it has no storage cap unlike metal/
 *   minerals/gas, so this is a plain subtraction rather than
 *   `applyResourceCost` (which doesn't touch `resources.scrap`).
 * - Parallel slot: always starts at `now`, never chained after another
 *   pending batch.
 */
export function planScrapRecycleQueueEntry(planet, pending, race, shipSelection, sciences, now, speedMultiplier, nextEventId) {
    const plantLevel = planet.buildings.recyclingPlant?.level ?? 0;
    if (plantLevel < 1) {
        throw new QueueError("failed-precondition", `${BUILDING_NAMES.recyclingPlant} has not been built`);
    }
    const selection = Object.entries(shipSelection)
        .filter((entry) => !!entry[1]);
    if (selection.length === 0) {
        throw new QueueError("failed-precondition", "No ships selected to recycle");
    }
    for (const [, quantity] of selection) {
        if (!Number.isInteger(quantity) || quantity < 1) {
            throw new QueueError("failed-precondition", "Ship quantities must be positive integers");
        }
    }
    const batches = pending.filter(isPendingScrapRecycle);
    if (batches.length >= plantLevel) {
        throw new QueueError("queue-full", `Recycling queue is full (${plantLevel} slot${plantLevel === 1 ? "" : "s"})`);
    }
    const resetAt = planet.scrapRecycleDaily?.resetAt;
    const windowExpired = !resetAt || resetAt.getTime() <= now.getTime();
    const dailyCount = windowExpired ? 0 : (planet.scrapRecycleDaily?.count ?? 0);
    if (dailyCount >= SCRAP_RECYCLE_DAILY_LIMIT) {
        throw new QueueError("queue-full", `Daily recycle limit reached (${SCRAP_RECYCLE_DAILY_LIMIT} batches per day)`);
    }
    const actualLevels = extractBuildingLevels(planet.buildings);
    for (const [key] of selection) {
        const shipId = Number(key);
        if (!checkShipRequirements(race, shipId, actualLevels, sciences)) {
            const def = getShipDefinition(race, shipId);
            throw new QueueError("failed-precondition", `Prerequisites for ${def.name} are not met`);
        }
    }
    const shipsRequested = Object.fromEntries(selection);
    // A recycled batch docks its ships just like a shipyard batch, so it is
    // bounded by free population at queue time (issue #119) — and
    // `calculateFreePopulation` already discounts the OTHER pending batches
    // of both kinds, which a multi-slot plant can easily have running.
    const freePopulation = calculateFreePopulation(race, planet.resources.population.capacity, planet.ships, pending, planet.shipsInFlight);
    const neededPopulation = calculateDockedPopulation(race, shipsRequested);
    if (neededPopulation > freePopulation) {
        throw new QueueError("insufficient-population", `Not enough free population for this recycle batch (needs ${neededPopulation}, ${Math.max(0, freePopulation)} free)`);
    }
    const scrapCost = recycleScrapRequired(race, shipsRequested, plantLevel);
    if (scrapCost > planet.resources.scrap.current) {
        throw new QueueError("insufficient-resources", `Insufficient ${RESOURCE_NAMES.scrap} for this recycle batch`);
    }
    const durationSeconds = selection.reduce((total, [key, quantity]) => {
        const shipId = Number(key);
        return total + calculateShipBuildTime(race, shipId, quantity, actualLevels.shipyard, actualLevels.automationCenter, speedMultiplier).totalSeconds;
    }, 0);
    const endDate = new Date(now.getTime() + durationSeconds * 1000);
    const event = {
        eventId: nextEventId,
        eventType: "scrapRecycle",
        createdDate: now,
        updatedDate: now,
        endDate,
        eventDetails: {
            ships: shipsRequested,
            scrapCost,
            // Always held for AUTO_DELIVERY_HOLD_HOURS after processing
            // finishes — the player may collect on demand any time from
            // `endDate` onward (`deliverScrapRecycleBatch`), or leave it to
            // auto-drop at this deadline (`replayEventsToNow`).
            deliverAt: new Date(endDate.getTime() + AUTO_DELIVERY_HOLD_HOURS * 3600000),
        },
    };
    const updatedPlanet = {
        ...planet,
        resources: {
            ...planet.resources,
            scrap: { current: planet.resources.scrap.current - scrapCost },
        },
        scrapRecycleDaily: {
            count: dailyCount + 1,
            resetAt: windowExpired ? new Date(now.getTime() + 24 * 3600 * 1000) : resetAt,
        },
    };
    return {
        planet: updatedPlanet,
        pending: [...pending, event],
        event,
        nextEventId: nextEventId + 1,
    };
}
/**
 * Cancel a pending scrapRecycle batch on an already-replayed planet.
 *
 * Refunds a uniformly-rolled 75-80% of the batch's paid scrap
 * (`SCRAP_RECYCLE_REFUND_MIN`/`MAX` — the roll is injected so this stays
 * deterministic; the functions layer passes real randomness). No reflow:
 * batches are independent parallel slots, so removing one doesn't move any
 * other batch's schedule. The refund amount is returned directly
 * (`refundedScrap`) rather than via `EventLogRecord.refund`, since
 * `ResourceCost` — that field's type — has no `scrap` key (scrap isn't a
 * queue-cost resource anywhere else in the engine).
 *
 * A batch stays pending past its own `endDate` — processing is done, it's
 * just holding for delivery (`deliverAt`, issue #21's 48-hour hold) — so it
 * can no longer be cancelled once that point is reached; there is nothing
 * left to refund, only to deliver (see {@link deliverScrapRecycleBatch}).
 */
export function cancelScrapRecycleEvent(planet, pending, eventId, now, refundRoll) {
    const target = pending.find((e) => e.eventId === eventId && isPendingScrapRecycle(e));
    if (!target || !isPendingScrapRecycle(target)) {
        throw new QueueError("event-not-found", `No pending recycle batch with eventId ${eventId}`);
    }
    if (now.getTime() >= target.endDate.getTime()) {
        throw new QueueError("failed-precondition", "This batch has already finished processing and is awaiting delivery — it can no longer be cancelled");
    }
    const clampedRoll = Math.min(1, Math.max(0, refundRoll));
    const refundRate = SCRAP_RECYCLE_REFUND_MIN + clampedRoll * (SCRAP_RECYCLE_REFUND_MAX - SCRAP_RECYCLE_REFUND_MIN);
    const refundedScrap = Math.floor(target.eventDetails.scrapCost * refundRate);
    const refundedPlanet = {
        ...planet,
        resources: {
            ...planet.resources,
            scrap: { current: planet.resources.scrap.current + refundedScrap },
        },
    };
    const cancelled = [{
            event: { ...target, cancelled: true, updatedDate: now },
            status: "cancelled",
            resolvedAt: now,
        }];
    const newPending = pending.filter((e) => e.eventId !== target.eventId);
    return { planet: refundedPlanet, pending: newPending, cancelled, refundedScrap };
}
/**
 * Deliver a pending scrapRecycle batch on demand, before its automatic
 * `deliverAt` deadline (issue #21's 48-hour hold). Docks the batch's ships
 * immediately, but only once processing has actually finished — `now >=
 * endDate`; requesting delivery any earlier is rejected with
 * `failed-precondition`. An unknown `eventId` (or one `replayEventsToNow`
 * has already auto-delivered past `deliverAt`) is rejected with
 * `event-not-found`.
 */
export function deliverScrapRecycleBatch(planet, pending, eventId, now) {
    const target = pending.find((e) => e.eventId === eventId && isPendingScrapRecycle(e));
    if (!target || !isPendingScrapRecycle(target)) {
        throw new QueueError("event-not-found", `No pending recycle batch with eventId ${eventId}`);
    }
    if (now.getTime() < target.endDate.getTime()) {
        throw new QueueError("failed-precondition", "This batch has not finished processing yet");
    }
    const dockedPlanet = applyScrapRecycle(planet, target);
    const delivered = [{ event: target, status: "applied", resolvedAt: now }];
    const newPending = pending.filter((e) => e.eventId !== target.eventId);
    return { planet: dockedPlanet, pending: newPending, delivered };
}
// ---------------------------------------------------------------------------
// Ship scrapping — the overpopulation lock's resolution action (issue #26).
// Deliberately runs no lock check of its own, mirroring destroyBuildingLevel:
// it must keep working regardless of the planet's overpopulation state,
// since it's the only way to resolve it (and a player may want to free up
// population pre-emptively, without ever actually being locked).
// ---------------------------------------------------------------------------
/**
 * Scrap a batch of docked ships for a partial resource refund.
 *
 * Rejects with failed-precondition if nothing was requested, or if any
 * requested ship type/quantity exceeds what's actually docked. Refunds 70%
 * (`REFUND_RATES.ship`) of the scrapped ships' build cost, capped at
 * storage — same rate/rounding as a cancelled shipBuild batch's unfinished
 * remainder, but flat rather than rolled: these units already exist, there
 * is nothing partial about scrapping them.
 */
export function scrapShips(planet, race, ships, now) {
    const requested = Object.entries(ships)
        .filter((entry) => !!entry[1]);
    if (requested.length === 0) {
        throw new QueueError("failed-precondition", "No ships selected to scrap");
    }
    let totalCost = {};
    let scrapped = planet;
    for (const [key, quantity] of requested) {
        if (!Number.isInteger(quantity) || quantity <= 0) {
            throw new QueueError("failed-precondition", `Invalid scrap quantity for ship ${key}`);
        }
        const shipId = Number(key);
        const docked = planet.ships?.[shipId] ?? 0;
        if (quantity > docked) {
            const def = getShipDefinition(race, shipId);
            throw new QueueError("failed-precondition", `Only ${docked} × ${def.name} are docked on this planet (requested ${quantity})`);
        }
        scrapped = undockShips(scrapped, shipId, quantity);
        const cost = calculateShipBuildCost(race, shipId, quantity);
        totalCost = {
            metal: (totalCost.metal ?? 0) + (cost.metal ?? 0),
            minerals: (totalCost.minerals ?? 0) + (cost.minerals ?? 0),
            gas: (totalCost.gas ?? 0) + (cost.gas ?? 0),
        };
    }
    const refund = calculateNominalRefundAtRates(totalCost, REFUND_RATES.ship, 1);
    const refundedPlanet = applyResourceRefundAtRates(scrapped, totalCost, REFUND_RATES.ship, 1, now);
    return { planet: refundedPlanet, refund };
}
// ---------------------------------------------------------------------------
// Science queueing — single global FIFO on the player profile's
// tickCheckpoints (not per-planet, not per-zone). See NOTE(scienceResearch)
// above `applyEvent`: applying the *effect* (bumping PlayerSciences) is a
// functions-layer concern, since `Planet` carries no science data.
// ---------------------------------------------------------------------------
/**
 * Validate and stage a science research on an already-replayed planet (the
 * planet whose resources pay for it — "Sciences can be researched on any of
 * a player's Planets", but the resulting level applies to all of them).
 *
 * - Queue limit counts pending scienceResearch events on the profile.
 * - Cost and max-level use the projected level (current + queued levels of
 *   this same science).
 * - Requirements (Research Center level, other sciences) use ACTUAL levels only
 *   — queued research never satisfies a requirement.
 * - Only one "additional" category science may ever be researched.
 * - The full cost is paid immediately from `planet`, mirroring buildings.
 * - Single FIFO: research starts when the queue's last pending item ends.
 */
export function planScienceQueueEntry(planet, profilePending, scienceId, scienceLevels, now, speedMultiplier, queueLimit, nextEventId) {
    const researches = profilePending.filter(isPendingResearch);
    if (researches.length >= queueLimit) {
        throw new QueueError("queue-full", `Research queue is full (${queueLimit} slots)`);
    }
    if (!canResearchAdditionalScience(scienceId, scienceLevels)) {
        throw new QueueError("failed-precondition", "Only one Additional science may be researched per player");
    }
    const currentLevel = scienceLevels[scienceId] ?? 0;
    const queuedLevels = researches.filter((e) => e.eventDetails.scienceId === scienceId).length;
    const projectedLevel = currentLevel + queuedLevels;
    const maxLevel = getMaxScienceLevel(scienceId);
    if (maxLevel > 0 && projectedLevel >= maxLevel) {
        throw new QueueError("failed-precondition", `${scienceId} is already at max level (counting queued upgrades)`);
    }
    const buildingLevels = extractBuildingLevels(planet.buildings);
    if (!checkScienceRequirements(scienceId, buildingLevels, scienceLevels)) {
        throw new QueueError("failed-precondition", `Prerequisites for ${scienceId} are not met`);
    }
    const requiredFreeEnergy = getScienceRequiredFreeEnergy(scienceId);
    if (!hasRequiredFreeEnergy(scienceId, planet.resources.energy.current)) {
        throw new QueueError("insufficient-resources", `${SCIENCE_DEFINITIONS[scienceId].name} requires ${requiredFreeEnergy.toLocaleString("en-US")} free Energy to start`);
    }
    const targetLevel = projectedLevel + 1;
    const cost = calculateScienceCost(scienceId, targetLevel);
    const affordable = canAffordCost(cost, {
        metal: planet.resources.metal.current,
        minerals: planet.resources.minerals.current,
        gas: planet.resources.gas.current,
        energy: planet.resources.energy.current,
        ozone: planet.state.ozone.current,
    });
    if (!affordable) {
        throw new QueueError("insufficient-resources", `Insufficient resources for ${scienceId}`);
    }
    const laboratoryLevel = buildingLevels.laboratory ?? 0;
    const queueTailMs = researches.reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    const startMs = Math.max(now.getTime(), queueTailMs);
    const durationSeconds = calculateScienceTime(scienceId, targetLevel, laboratoryLevel, speedMultiplier);
    const event = {
        eventId: nextEventId,
        eventType: "scienceResearch",
        createdDate: now,
        updatedDate: now,
        endDate: new Date(startMs + durationSeconds * 1000),
        eventDetails: {
            scienceId,
            targetLevel,
            planetId: planet.id,
            paidCost: cost,
            durationSeconds,
            startDate: new Date(startMs),
        },
    };
    return {
        planet: applyResourceCost(planet, cost, now),
        pending: [...profilePending, event],
        event,
        nextEventId: nextEventId + 1,
    };
}
/**
 * Cancel a pending scienceResearch event and reflow the single global queue.
 *
 * Research levels are sequential just like buildings: the target and every
 * queued HIGHER level of the same science are cancelled too (level
 * continuity — L12/L13 can't complete if L11 didn't), each refunded at 60%.
 * Other sciences' queued entries are untouched.
 *
 * The caller is responsible for crediting each cancelled record's refund to
 * its own `eventDetails.planetId` (the planet that originally paid) — that
 * planet isn't passed here since cancellation is planet-agnostic at the
 * queue level, and a cascade's levels could in principle have been paid by
 * different planets.
 */
export function cancelScienceEvent(profilePending, eventId, now) {
    const target = profilePending.find((e) => e.eventId === eventId && isPendingResearch(e));
    if (!target || !isPendingResearch(target)) {
        throw new QueueError("event-not-found", `No pending research with eventId ${eventId}`);
    }
    const cascade = profilePending
        .filter(isPendingResearch)
        .filter((e) => e.eventId !== target.eventId &&
        e.eventDetails.scienceId === target.eventDetails.scienceId &&
        e.eventDetails.targetLevel > target.eventDetails.targetLevel)
        .sort(byEndDateThenId);
    const cancelled = [target, ...cascade].map((e) => ({
        event: { ...e, cancelled: true, updatedDate: now },
        status: "cancelled",
        resolvedAt: now,
        refund: calculateNominalRefund(e.eventDetails.paidCost, "research"),
    }));
    const removedIds = new Set(cancelled.map((r) => r.event.eventId));
    const survivors = profilePending.filter((e) => !removedIds.has(e.eventId));
    const newPending = reflowResearchQueue(survivors, now);
    return {
        pending: newPending,
        cancelled,
    };
}
/**
 * Recompute start/end dates of the single global research queue after a
 * removal. Started items are immutable anchors; each later item starts when
 * its predecessor ends (or `now`, whichever is later).
 */
function reflowResearchQueue(pending, now) {
    const researches = pending.filter(isPendingResearch).sort(byEndDateThenId);
    const others = pending.filter((e) => !isPendingResearch(e));
    const reflowed = [];
    let cursorMs = now.getTime();
    for (const e of researches) {
        if (e.eventDetails.startDate.getTime() <= now.getTime()) {
            reflowed.push(e);
            cursorMs = Math.max(cursorMs, e.endDate.getTime());
        }
        else if (e.eventDetails.startDate.getTime() === cursorMs) {
            reflowed.push(e);
            cursorMs = e.endDate.getTime();
        }
        else {
            const endMs = cursorMs + e.eventDetails.durationSeconds * 1000;
            reflowed.push({
                ...e,
                updatedDate: now,
                endDate: new Date(endMs),
                eventDetails: { ...e.eventDetails, startDate: new Date(cursorMs) },
            });
            cursorMs = endMs;
        }
    }
    return [...reflowed, ...others];
}
// ---------------------------------------------------------------------------
// Ship-upgrade queueing — single global FIFO on the player profile's
// tickCheckpoints (not per-planet), same shape as science queueing above.
// See NOTE(scienceResearch)/shipUpgrade comment above `applyEvent`: applying
// the *effect* (bumping the player's ship-upgrade levels) is a
// functions-layer concern, since `Planet` carries no such field.
// ---------------------------------------------------------------------------
/**
 * Validate and stage a ship upgrade on an already-replayed planet (the
 * planet whose resources pay for it — the resulting level applies to every
 * planet's ships of that type, "Upgrades are global").
 *
 * - Queue limit counts pending shipUpgrade events on the profile.
 * - Cost and max-level use the projected level (current + queued levels of
 *   this same ship).
 * - `lane` (Team planet only): the queue limit, the projected level and the
 *   queue tail consider ONLY pending upgrades queued in that race's lane, and
 *   the new event is stamped with the lane and the member who queued it.
 *   Without it, only events carrying no lane count — a player's own queue.
 * - Requirements are the SAME ones required to build the ship in the first
 *   place (`checkShipRequirements`, actual levels only — issue #23: "a ship
 *   cannot be upgraded if it cannot be built"), plus Tech Center (`techCenter`)
 *   must be at least level 1 on the paying planet.
 * - The full cost is paid immediately from `planet`, mirroring science.
 * - Single FIFO: the upgrade starts when the queue's last pending item ends.
 */
export function planShipUpgradeQueueEntry(planet, profilePending, race, shipId, shipUpgradeLevels, sciences, now, speedMultiplier, queueLimit, nextEventId, lane) {
    const def = getShipDefinition(race, shipId);
    if (!isShipUpgradable(shipId)) {
        throw new QueueError("failed-precondition", `${def.name} cannot be upgraded`);
    }
    const upgrades = profilePending
        .filter(isPendingShipUpgrade)
        .filter((e) => e.eventDetails.queueLane === lane?.race);
    if (upgrades.length >= queueLimit) {
        throw new QueueError("queue-full", `Ship upgrade queue is full (${queueLimit} slots)`);
    }
    const currentLevel = shipUpgradeLevels[shipId] ?? 0;
    const queuedLevels = upgrades.filter((e) => e.eventDetails.shipType === shipId).length;
    const projectedLevel = currentLevel + queuedLevels;
    if (projectedLevel >= SHIP_UPGRADE_MAX_LEVEL) {
        throw new QueueError("failed-precondition", `${def.name} is already at max upgrade level (counting queued upgrades)`);
    }
    const buildingLevels = extractBuildingLevels(planet.buildings);
    if (!checkShipRequirements(race, shipId, buildingLevels, sciences)) {
        throw new QueueError("failed-precondition", `Prerequisites for ${def.name} are not met`);
    }
    const techCenterLevel = buildingLevels.techCenter ?? 0;
    if (techCenterLevel < 1) {
        throw new QueueError("failed-precondition", `${BUILDING_NAMES.techCenter} at Level 1 is required to upgrade ships`);
    }
    const targetLevel = projectedLevel + 1;
    const cost = calculateShipUpgradeCost(race, shipId, targetLevel);
    const affordable = canAffordCost(cost, {
        metal: planet.resources.metal.current,
        minerals: planet.resources.minerals.current,
        gas: planet.resources.gas.current,
        energy: planet.resources.energy.current,
        ozone: planet.state.ozone.current,
    });
    if (!affordable) {
        throw new QueueError("insufficient-resources", `Insufficient resources for ${def.name} upgrade`);
    }
    const queueTailMs = upgrades.reduce((tail, e) => Math.max(tail, e.endDate.getTime()), 0);
    const startMs = Math.max(now.getTime(), queueTailMs);
    const durationSeconds = calculateShipUpgradeTime(race, shipId, targetLevel, techCenterLevel, speedMultiplier);
    const event = {
        eventId: nextEventId,
        eventType: "shipUpgrade",
        createdDate: now,
        updatedDate: now,
        endDate: new Date(startMs + durationSeconds * 1000),
        eventDetails: {
            shipType: shipId,
            targetLevel,
            planetId: planet.id,
            paidCost: cost,
            durationSeconds,
            startDate: new Date(startMs),
            ...(lane ? { queueLane: lane.race, queuedByUserId: lane.queuedByUserId } : {}),
        },
    };
    return {
        planet: applyResourceCost(planet, cost, now),
        pending: [...profilePending, event],
        event,
        nextEventId: nextEventId + 1,
    };
}
/**
 * Every pending ship upgrade that cancelling `eventId` removes: the target
 * first, then each higher queued level of the same ship in the same lane in
 * end order. Empty when `eventId` is not a pending ship upgrade.
 */
export function listShipUpgradeCancelSet(profilePending, eventId) {
    const target = profilePending.find((e) => e.eventId === eventId && isPendingShipUpgrade(e));
    if (!target || !isPendingShipUpgrade(target))
        return [];
    const cascade = profilePending
        .filter(isPendingShipUpgrade)
        .filter((e) => e.eventId !== target.eventId &&
        e.eventDetails.queueLane === target.eventDetails.queueLane &&
        e.eventDetails.shipType === target.eventDetails.shipType &&
        e.eventDetails.targetLevel > target.eventDetails.targetLevel)
        .sort(byEndDateThenId);
    return [target, ...cascade];
}
/**
 * Whether `userId` may cancel the queued upgrade `eventId`. The leader may
 * cancel anything; anyone else only when every upgrade the cancellation
 * removes (the target and each higher queued level of that ship in its lane)
 * was queued by them. An upgrade with no recorded member counts as the
 * leader's own.
 */
export function canCancelTeamPlanetShipUpgrade(pending, eventId, userId, isLeader) {
    const toCancel = listShipUpgradeCancelSet(pending, eventId);
    if (toCancel.length === 0)
        return false;
    return isLeader || toCancel.every((e) => e.eventDetails.queuedByUserId === userId);
}
/**
 * Cancel a pending shipUpgrade event and reflow its queue (the single global
 * queue, or one race lane of a Team planet's Tech Center).
 *
 * Upgrade levels are sequential just like buildings/science: the target and
 * every queued HIGHER level of the same ship are cancelled too (level
 * continuity), each refunded at a uniformly-rolled 60-80% ("Cancel refunds
 * 60–80% of resources") — the SAME roll applies to every record in the
 * cascade (mirrors the fixed-rate cascades for buildings/science; only the
 * rate itself is random here). The roll is injected so this stays
 * deterministic; the functions layer passes real randomness.
 *
 * The caller is responsible for crediting each cancelled record's refund to
 * its own `eventDetails.planetId` (the planet that originally paid) — same
 * reasoning as `cancelScienceEvent`.
 */
export function cancelShipUpgradeEvent(profilePending, eventId, now, refundRoll) {
    const toCancel = listShipUpgradeCancelSet(profilePending, eventId);
    if (toCancel.length === 0) {
        throw new QueueError("event-not-found", `No pending ship upgrade with eventId ${eventId}`);
    }
    const refundRate = calculateShipUpgradeRefundRate(refundRoll);
    const cancelled = toCancel.map((e) => ({
        event: { ...e, cancelled: true, updatedDate: now },
        status: "cancelled",
        resolvedAt: now,
        refund: calculateNominalRefundAtRates(e.eventDetails.paidCost, refundRate, refundRate),
    }));
    const removedIds = new Set(cancelled.map((r) => r.event.eventId));
    const survivors = profilePending.filter((e) => !removedIds.has(e.eventId));
    const newPending = reflowShipUpgradeQueue(survivors, now);
    return {
        pending: newPending,
        cancelled,
    };
}
/**
 * Recompute start/end dates of the ship-upgrade queue after a removal —
 * independently per lane (a player's single queue is the lane-less one; a
 * Team planet has one lane per race). Started items are immutable anchors;
 * each later item starts when its predecessor ends (or `now`, whichever is
 * later). Mirrors {@link reflowResearchQueue}.
 */
function reflowShipUpgradeQueue(pending, now) {
    const upgrades = pending.filter(isPendingShipUpgrade).sort(byEndDateThenId);
    const others = pending.filter((e) => !isPendingShipUpgrade(e));
    const lanes = new Map();
    for (const e of upgrades) {
        const lane = lanes.get(e.eventDetails.queueLane);
        if (lane)
            lane.push(e);
        else
            lanes.set(e.eventDetails.queueLane, [e]);
    }
    const reflowed = [];
    for (const laneEvents of lanes.values()) {
        let cursorMs = now.getTime();
        for (const e of laneEvents) {
            if (e.eventDetails.startDate.getTime() <= now.getTime()) {
                reflowed.push(e);
                cursorMs = Math.max(cursorMs, e.endDate.getTime());
            }
            else if (e.eventDetails.startDate.getTime() === cursorMs) {
                reflowed.push(e);
                cursorMs = e.endDate.getTime();
            }
            else {
                const endMs = cursorMs + e.eventDetails.durationSeconds * 1000;
                reflowed.push({
                    ...e,
                    updatedDate: now,
                    endDate: new Date(endMs),
                    eventDetails: { ...e.eventDetails, startDate: new Date(cursorMs) },
                });
                cursorMs = endMs;
            }
        }
    }
    return [...reflowed, ...others];
}
function remainingSecondsOrThrow(event, now, notFoundMessage) {
    const remainingSeconds = (event.endDate.getTime() - now.getTime()) / 1000;
    if (remainingSeconds <= 0) {
        throw new QueueError("failed-precondition", notFoundMessage);
    }
    return remainingSeconds;
}
/**
 * Instantly complete a pending buildingBuild event and reflow its zone's
 * FIFO. Only the target's OWN duration collapses to zero — an item still
 * waiting behind an earlier one in the same zone does not jump ahead of it
 * (building levels must stay continuous); it simply becomes a zero-length
 * link in the chain, so everything queued after it shifts earlier by
 * whatever time this item itself would have taken, once its turn arrives.
 * An item that has already started (the zone's current in-progress build)
 * has no predecessor to wait on, so it still completes immediately. Rejects
 * an item that's already been rushed (`durationSeconds === 0`) — there is
 * nothing left to shorten, so a second rush would just be a wasted charge.
 */
export function rushBuildingEvent(pending, eventId, now) {
    const target = pending.find((e) => e.eventId === eventId && isPendingBuild(e));
    if (!target || !isPendingBuild(target)) {
        throw new QueueError("event-not-found", `No pending build with eventId ${eventId}`);
    }
    if (target.eventDetails.durationSeconds === 0) {
        throw new QueueError("failed-precondition", "This build has already been rushed");
    }
    const remainingSeconds = remainingSecondsOrThrow(target, now, "This build has already finished");
    // A not-yet-started item only has its OWN duration collapsed below — it
    // still waits behind its predecessor — so it's priced on that duration
    // alone, not on `remainingSeconds` (which includes the predecessor's
    // wait). An already-started item completes immediately, so it's priced
    // on the full remaining time it actually removes.
    const started = target.eventDetails.startDate.getTime() <= now.getTime();
    const cost = calculateRushCost(started ? remainingSeconds : target.eventDetails.durationSeconds);
    const collapsedEnd = started ? now : target.eventDetails.startDate;
    const patched = pending.map((e) => e.eventId === eventId && isPendingBuild(e)
        ? { ...e, endDate: collapsedEnd, updatedDate: now, eventDetails: { ...e.eventDetails, durationSeconds: 0 } }
        : e);
    return { pending: reflowZoneQueues(patched, now), cost };
}
/**
 * Instantly complete a pending shipBuild batch and reflow its lane's FIFO.
 * Only the target's OWN duration collapses to zero — a batch still waiting
 * behind an earlier one in the same lane does not jump ahead of it; it
 * becomes a zero-length link in the chain, so everything queued after it in
 * the lane shifts earlier by this batch's own duration once its turn
 * arrives. A batch that's already started (the lane's current in-progress
 * build) has no predecessor to wait on, so it still completes immediately.
 * Rejects a batch that's already been rushed (`durationSeconds === 0`) —
 * there is nothing left to shorten. Rejects a Robots delivery-group member
 * (`groupId` set) — its `deliverAt`
 * hold was computed from the GROUP's completion, not this one batch's own,
 * so rushing a single member can't be expressed as a simple time-skip here.
 */
export function rushShipBuildEvent(pending, eventId, now) {
    const target = pending.find((e) => e.eventId === eventId && isPendingShipBuild(e));
    if (!target || !isPendingShipBuild(target)) {
        throw new QueueError("event-not-found", `No pending ship build with eventId ${eventId}`);
    }
    if (target.eventDetails.groupId !== undefined) {
        throw new QueueError("failed-precondition", "A grouped ship-build batch cannot be rushed individually");
    }
    if (target.eventDetails.durationSeconds === 0) {
        throw new QueueError("failed-precondition", "This batch has already been rushed");
    }
    const remainingSeconds = remainingSecondsOrThrow(target, now, "This batch has already finished construction");
    // See rushBuildingEvent's comment above — a not-yet-started batch is
    // priced on its own duration, not the predecessor-inclusive remaining time.
    const started = target.eventDetails.startDate.getTime() <= now.getTime();
    const cost = calculateRushCost(started ? remainingSeconds : target.eventDetails.durationSeconds);
    const collapsedEnd = started ? now : target.eventDetails.startDate;
    const patched = pending.map((e) => e.eventId === eventId && isPendingShipBuild(e)
        ? { ...e, endDate: collapsedEnd, updatedDate: now, eventDetails: { ...e.eventDetails, durationSeconds: 0 } }
        : e);
    return { pending: reflowShipQueue(patched, now), cost };
}
/**
 * Instantly complete a pending scrapRecycle batch. Parallel slots need no
 * reflow (see the "Recovery Center queueing" section above), but the
 * automatic `deliverAt` hold — originally `AUTO_DELIVERY_HOLD_HOURS` after
 * `endDate` — is shifted earlier by the same amount `endDate` moved, so the
 * hold still lasts its full duration counted from the batch's (now earlier)
 * actual completion, rather than collapsing to zero.
 */
export function rushScrapRecycleEvent(pending, eventId, now) {
    const target = pending.find((e) => e.eventId === eventId && isPendingScrapRecycle(e));
    if (!target || !isPendingScrapRecycle(target)) {
        throw new QueueError("event-not-found", `No pending recycle batch with eventId ${eventId}`);
    }
    const remainingSeconds = remainingSecondsOrThrow(target, now, "This batch has already finished processing");
    const cost = calculateRushCost(remainingSeconds);
    const deliverDelayMs = target.eventDetails.deliverAt
        ? target.eventDetails.deliverAt.getTime() - target.endDate.getTime()
        : 0;
    const newPending = pending.map((e) => {
        if (e.eventId !== eventId || !isPendingScrapRecycle(e))
            return e;
        return {
            ...e,
            endDate: now,
            updatedDate: now,
            eventDetails: { ...e.eventDetails, deliverAt: new Date(now.getTime() + deliverDelayMs) },
        };
    });
    return { pending: newPending, cost };
}
/**
 * Instantly complete a pending scienceResearch event and reflow the single
 * global queue. Rejects `NON_RUSHABLE_SCIENCE_IDS` (`gravitationalManipulators`).
 * Only the target's OWN duration collapses to zero — research still waiting
 * behind an earlier item does not jump ahead of it; it becomes a
 * zero-length link in the chain, so everything queued after it shifts
 * earlier by this item's own duration once its turn arrives. An item
 * that's already started (the queue's current in-progress research) has no
 * predecessor to wait on, so it still completes immediately. Rejects
 * research that's already been rushed (`durationSeconds === 0`) — there is
 * nothing left to shorten.
 */
export function rushScienceEvent(profilePending, eventId, now) {
    const target = profilePending.find((e) => e.eventId === eventId && isPendingResearch(e));
    if (!target || !isPendingResearch(target)) {
        throw new QueueError("event-not-found", `No pending research with eventId ${eventId}`);
    }
    if (!isScienceRushable(target.eventDetails.scienceId)) {
        throw new QueueError("failed-precondition", `${target.eventDetails.scienceId} cannot be rushed`);
    }
    if (target.eventDetails.durationSeconds === 0) {
        throw new QueueError("failed-precondition", "This research has already been rushed");
    }
    const remainingSeconds = remainingSecondsOrThrow(target, now, "This research has already finished");
    // See rushBuildingEvent's comment above — a not-yet-started item is
    // priced on its own duration, not the predecessor-inclusive remaining time.
    const started = target.eventDetails.startDate.getTime() <= now.getTime();
    const cost = calculateRushCost(started ? remainingSeconds : target.eventDetails.durationSeconds);
    const collapsedEnd = started ? now : target.eventDetails.startDate;
    const patched = profilePending.map((e) => e.eventId === eventId && isPendingResearch(e)
        ? { ...e, endDate: collapsedEnd, updatedDate: now, eventDetails: { ...e.eventDetails, durationSeconds: 0 } }
        : e);
    return { pending: reflowResearchQueue(patched, now), cost };
}
/**
 * Instantly complete a pending shipUpgrade event and reflow the single
 * global queue. Only the target's OWN duration collapses to zero — an
 * upgrade still waiting behind an earlier one does not jump ahead of it; it
 * becomes a zero-length link in the chain, so everything queued after it
 * shifts earlier by this item's own duration once its turn arrives. An item
 * that's already started (the queue's current in-progress upgrade) has no
 * predecessor to wait on, so it still completes immediately. Rejects an
 * upgrade that's already been rushed (`durationSeconds === 0`) — there is
 * nothing left to shorten.
 */
export function rushShipUpgradeEvent(profilePending, eventId, now) {
    const target = profilePending.find((e) => e.eventId === eventId && isPendingShipUpgrade(e));
    if (!target || !isPendingShipUpgrade(target)) {
        throw new QueueError("event-not-found", `No pending ship upgrade with eventId ${eventId}`);
    }
    if (target.eventDetails.durationSeconds === 0) {
        throw new QueueError("failed-precondition", "This ship upgrade has already been rushed");
    }
    const remainingSeconds = remainingSecondsOrThrow(target, now, "This ship upgrade has already finished");
    // See rushBuildingEvent's comment above — a not-yet-started item is
    // priced on its own duration, not the predecessor-inclusive remaining time.
    const started = target.eventDetails.startDate.getTime() <= now.getTime();
    const cost = calculateRushCost(started ? remainingSeconds : target.eventDetails.durationSeconds);
    const collapsedEnd = started ? now : target.eventDetails.startDate;
    const patched = profilePending.map((e) => e.eventId === eventId && isPendingShipUpgrade(e)
        ? { ...e, endDate: collapsedEnd, updatedDate: now, eventDetails: { ...e.eventDetails, durationSeconds: 0 } }
        : e);
    return { pending: reflowShipUpgradeQueue(patched, now), cost };
}
//# sourceMappingURL=tickEvents.js.map